import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import Data, { AddressBlock } from 'models/DataModel';
import { SavedAnswer } from 'models/SavedAnswerModel';
import type { DataMergeConflict } from 'utils/orderItemDataMerge';
import {
  clearOrderItemDraft,
  getOrderItemDraftCacheKey,
  loadOrderItemDraft,
  OrderItemDraftCacheIdentity,
  OrderItemDraftDirtyFields,
  PersistedOrderItemDraft,
  saveOrderItemDraft
} from 'utils/orderItemDraftCache';

export type DraftAnswerValues = Record<string, string>;
export type DraftTokenValues = Record<string, Record<string, any>>;

export interface OrderItemDraftDirtyState {
  answers: Record<string, boolean>;
  tokens: Record<string, Record<string, boolean>>;
  address: boolean;
  attachments: boolean;
}

export interface OrderItemDraftState {
  answers: DraftAnswerValues;
  tokens: DraftTokenValues;
  address: AddressBlock;
  attachments: string[];
  dirty: OrderItemDraftDirtyState;
  conflicts: DataMergeConflict[];
  baseETag: string | null;
  lastPersistedSnapshot: Data | null;
}

export type OrderItemDraftAction =
  | { type: 'serverSnapshotReceived'; snapshot: Data; etag: string | null }
  | { type: 'snapshotPersisted'; snapshot: Data; etag: string | null }
  | { type: 'draftRestored'; draft: PersistedOrderItemDraft }
  | { type: 'mergeConflictsDetected'; conflicts: DataMergeConflict[] }
  | { type: 'mergeConflictsCleared' }
  | { type: 'answerChanged'; questionId: string; value: string }
  | { type: 'tokenChanged'; questionId: string; token: string; value: any }
  | { type: 'addressChanged'; value: string }
  | { type: 'attachmentsChanged'; attachments: string[] };

const EmptyAddress: AddressBlock = { FormattedAddress: '' };

const hasOwn = (object: object, key: PropertyKey): boolean => Object.prototype.hasOwnProperty.call(object, key);

const createCleanDirtyState = (): OrderItemDraftDirtyState => ({
  answers: {},
  tokens: {},
  address: false,
  attachments: false
});

const cloneDirtyState = (dirty: OrderItemDraftDirtyFields): OrderItemDraftDirtyState => ({
  answers: { ...dirty.answers },
  tokens: Object.entries(dirty.tokens).reduce<Record<string, Record<string, boolean>>>((tokens, [questionId, dirtyTokens]) => {
    tokens[questionId] = { ...dirtyTokens };
    return tokens;
  }, {}),
  address: dirty.address,
  attachments: dirty.attachments
});

export const hasDirtyFields = (dirty: OrderItemDraftDirtyState): boolean =>
  dirty.address ||
  dirty.attachments ||
  Object.values(dirty.answers).some(Boolean) ||
  Object.values(dirty.tokens).some((tokens) => Object.values(tokens).some(Boolean));

export const createOrderItemDraftInitialState = (): OrderItemDraftState => ({
  answers: {},
  tokens: {},
  address: { ...EmptyAddress },
  attachments: [],
  dirty: createCleanDirtyState(),
  conflicts: [],
  baseETag: null,
  lastPersistedSnapshot: null
});

const splitSavedAnswers = (
  savedAnswers: Record<string, SavedAnswer> | undefined
): { answers: DraftAnswerValues; tokens: DraftTokenValues } => {
  const answers: DraftAnswerValues = {};
  const tokens: DraftTokenValues = {};

  Object.entries(savedAnswers ?? {}).forEach(([questionId, savedAnswer]) => {
    answers[questionId] = savedAnswer.Value;

    if (savedAnswer.Tokens && Object.keys(savedAnswer.Tokens).length > 0) {
      tokens[questionId] = { ...savedAnswer.Tokens };
    }
  });

  return { answers, tokens };
};

export const composeSavedAnswers = (answers: DraftAnswerValues, tokens: DraftTokenValues): Record<string, SavedAnswer> => {
  const savedAnswers: Record<string, SavedAnswer> = {};

  Object.entries(answers).forEach(([questionId, value]) => {
    const questionTokens = tokens[questionId] ?? {};

    savedAnswers[questionId] =
      Object.keys(questionTokens).length > 0
        ? {
            Value: value,
            Tokens: { ...questionTokens }
          }
        : {
            Value: value
          };
  });

  return savedAnswers;
};

const cloneData = (snapshot: Data): Data => {
  const { answers, tokens } = splitSavedAnswers(snapshot.Answers);

  return {
    ...snapshot,
    Address: { ...(snapshot.Address ?? EmptyAddress) },
    Answers: composeSavedAnswers(answers, tokens),
    Attachments: [...(snapshot.Attachments ?? [])]
  };
};

const mergeAnswers = (current: DraftAnswerValues, incoming: DraftAnswerValues, dirty: Record<string, boolean>): DraftAnswerValues => {
  const merged: DraftAnswerValues = {};
  const questionIds = new Set([...Object.keys(current), ...Object.keys(incoming)]);

  questionIds.forEach((questionId) => {
    if (dirty[questionId] && hasOwn(current, questionId)) {
      merged[questionId] = current[questionId];
      return;
    }

    if (hasOwn(incoming, questionId)) {
      merged[questionId] = incoming[questionId];
    }
  });

  return merged;
};

const mergeTokens = (
  current: DraftTokenValues,
  incoming: DraftTokenValues,
  dirty: Record<string, Record<string, boolean>>
): DraftTokenValues => {
  const merged: DraftTokenValues = {};
  const questionIds = new Set([...Object.keys(current), ...Object.keys(incoming)]);

  questionIds.forEach((questionId) => {
    const currentTokens = current[questionId] ?? {};
    const incomingTokens = incoming[questionId] ?? {};
    const dirtyTokens = dirty[questionId] ?? {};
    const tokenKeys = new Set([...Object.keys(currentTokens), ...Object.keys(incomingTokens)]);
    const mergedTokens: Record<string, any> = {};

    tokenKeys.forEach((token) => {
      if (dirtyTokens[token] && hasOwn(currentTokens, token)) {
        mergedTokens[token] = currentTokens[token];
        return;
      }

      if (hasOwn(incomingTokens, token)) {
        mergedTokens[token] = incomingTokens[token];
      }
    });

    if (Object.keys(mergedTokens).length > 0) {
      merged[questionId] = mergedTokens;
    }
  });

  return merged;
};

const reconcilePersistedAnswers = (
  current: DraftAnswerValues,
  persisted: DraftAnswerValues
): { answers: DraftAnswerValues; dirtyAnswers: Record<string, boolean> } => {
  const answers: DraftAnswerValues = {};
  const dirtyAnswers: Record<string, boolean> = {};
  const questionIds = new Set([...Object.keys(current), ...Object.keys(persisted)]);

  questionIds.forEach((questionId) => {
    if (hasOwn(current, questionId) && current[questionId] !== persisted[questionId]) {
      answers[questionId] = current[questionId];
      dirtyAnswers[questionId] = true;
      return;
    }

    if (hasOwn(persisted, questionId)) {
      answers[questionId] = persisted[questionId];
    }
  });

  return { answers, dirtyAnswers };
};

const reconcilePersistedTokens = (
  current: DraftTokenValues,
  persisted: DraftTokenValues
): { tokens: DraftTokenValues; dirtyTokens: Record<string, Record<string, boolean>> } => {
  const tokens: DraftTokenValues = {};
  const dirtyTokens: Record<string, Record<string, boolean>> = {};
  const questionIds = new Set([...Object.keys(current), ...Object.keys(persisted)]);

  questionIds.forEach((questionId) => {
    const currentTokens = current[questionId] ?? {};
    const persistedTokens = persisted[questionId] ?? {};
    const tokenKeys = new Set([...Object.keys(currentTokens), ...Object.keys(persistedTokens)]);
    const resolvedTokens: Record<string, any> = {};
    const resolvedDirtyTokens: Record<string, boolean> = {};

    tokenKeys.forEach((token) => {
      if (hasOwn(currentTokens, token) && currentTokens[token] !== persistedTokens[token]) {
        resolvedTokens[token] = currentTokens[token];
        resolvedDirtyTokens[token] = true;
        return;
      }

      if (hasOwn(persistedTokens, token)) {
        resolvedTokens[token] = persistedTokens[token];
      }
    });

    if (Object.keys(resolvedTokens).length > 0) {
      tokens[questionId] = resolvedTokens;
    }

    if (Object.keys(resolvedDirtyTokens).length > 0) {
      dirtyTokens[questionId] = resolvedDirtyTokens;
    }
  });

  return { tokens, dirtyTokens };
};

const attachmentsEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areDataEqual = (left: Data, right: Data): boolean => isEqual(left, right);

const hydrateCleanState = (snapshot: Data, etag: string | null): OrderItemDraftState => {
  const { answers, tokens } = splitSavedAnswers(snapshot.Answers);

  return {
    answers,
    tokens,
    address: { ...(snapshot.Address ?? EmptyAddress) },
    attachments: [...(snapshot.Attachments ?? [])],
    dirty: createCleanDirtyState(),
    conflicts: [],
    baseETag: etag,
    lastPersistedSnapshot: cloneData(snapshot)
  };
};

const restoreDraftState = (state: OrderItemDraftState, draft: PersistedOrderItemDraft): OrderItemDraftState => {
  const { answers, tokens } = splitSavedAnswers(draft.draftData.Answers);

  return {
    ...state,
    answers,
    tokens,
    address: { ...(draft.draftData.Address ?? EmptyAddress) },
    attachments: [...(draft.draftData.Attachments ?? [])],
    dirty: cloneDirtyState(draft.dirtyFields),
    conflicts: []
  };
};

const clearAnswerConflict = (conflicts: DataMergeConflict[], questionId: string): DataMergeConflict[] =>
  conflicts.filter((conflict) => conflict.kind !== 'answer' || conflict.questionId !== questionId);

const clearTokenConflict = (conflicts: DataMergeConflict[], questionId: string, token: string): DataMergeConflict[] =>
  conflicts.filter((conflict) => conflict.kind !== 'token' || conflict.questionId !== questionId || conflict.token !== token);

const clearConflictKind = (conflicts: DataMergeConflict[], kind: DataMergeConflict['kind']): DataMergeConflict[] =>
  conflicts.filter((conflict) => conflict.kind !== kind);

export const getOrderItemDraftData = (state: OrderItemDraftState): Data | null => {
  if (!state.lastPersistedSnapshot) {
    return null;
  }

  return {
    ...cloneData(state.lastPersistedSnapshot),
    Address: { ...state.address },
    Answers: composeSavedAnswers(state.answers, state.tokens),
    Attachments: [...state.attachments]
  };
};

export const orderItemDraftReducer = (state: OrderItemDraftState, action: OrderItemDraftAction): OrderItemDraftState => {
  switch (action.type) {
    case 'serverSnapshotReceived': {
      if (!state.lastPersistedSnapshot) {
        return hydrateCleanState(action.snapshot, action.etag);
      }

      const incoming = splitSavedAnswers(action.snapshot.Answers);

      return {
        ...state,
        answers: mergeAnswers(state.answers, incoming.answers, state.dirty.answers),
        tokens: mergeTokens(state.tokens, incoming.tokens, state.dirty.tokens),
        address: state.dirty.address ? state.address : { ...(action.snapshot.Address ?? EmptyAddress) },
        attachments: state.dirty.attachments ? state.attachments : [...(action.snapshot.Attachments ?? [])],
        conflicts: [],
        baseETag: action.etag,
        lastPersistedSnapshot: cloneData(action.snapshot)
      };
    }

    case 'snapshotPersisted': {
      const persisted = hydrateCleanState(action.snapshot, action.etag);
      const { answers, dirtyAnswers } = reconcilePersistedAnswers(state.answers, persisted.answers);
      const { tokens, dirtyTokens } = reconcilePersistedTokens(state.tokens, persisted.tokens);
      const keepDirtyAddress = state.address.FormattedAddress !== persisted.address.FormattedAddress;
      const keepDirtyAttachments = !attachmentsEqual(state.attachments, persisted.attachments);

      return {
        ...persisted,
        answers,
        tokens,
        address: keepDirtyAddress ? state.address : persisted.address,
        attachments: keepDirtyAttachments ? state.attachments : persisted.attachments,
        dirty: {
          answers: dirtyAnswers,
          tokens: dirtyTokens,
          address: keepDirtyAddress,
          attachments: keepDirtyAttachments
        },
        conflicts: []
      };
    }

    case 'draftRestored':
      return restoreDraftState(state, action.draft);

    case 'mergeConflictsDetected':
      return {
        ...state,
        conflicts: [...action.conflicts]
      };

    case 'mergeConflictsCleared':
      return {
        ...state,
        conflicts: []
      };

    case 'answerChanged':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: action.value
        },
        dirty: {
          ...state.dirty,
          answers: {
            ...state.dirty.answers,
            [action.questionId]: true
          }
        },
        conflicts: clearAnswerConflict(state.conflicts, action.questionId)
      };

    case 'tokenChanged':
      return {
        ...state,
        tokens: {
          ...state.tokens,
          [action.questionId]: {
            ...(state.tokens[action.questionId] ?? {}),
            [action.token]: action.value
          }
        },
        dirty: {
          ...state.dirty,
          tokens: {
            ...state.dirty.tokens,
            [action.questionId]: {
              ...(state.dirty.tokens[action.questionId] ?? {}),
              [action.token]: true
            }
          }
        },
        conflicts: clearTokenConflict(state.conflicts, action.questionId, action.token)
      };

    case 'addressChanged':
      return {
        ...state,
        address: {
          ...state.address,
          FormattedAddress: action.value
        },
        dirty: {
          ...state.dirty,
          address: true
        },
        conflicts: clearConflictKind(state.conflicts, 'address')
      };

    case 'attachmentsChanged':
      return {
        ...state,
        attachments: [...action.attachments],
        dirty: {
          ...state.dirty,
          attachments: true
        },
        conflicts: clearConflictKind(state.conflicts, 'attachments')
      };

    default:
      return state;
  }
};

export const useOrderItemDraft = (identity?: OrderItemDraftCacheIdentity | null) => {
  const [state, dispatch] = useReducer(orderItemDraftReducer, undefined, createOrderItemDraftInitialState);
  const [pendingDraft, setPendingDraft] = useState<PersistedOrderItemDraft | null>(null);
  const loadedDraftKeyRef = useRef<string | null>(null);
  const clearWhenCleanRef = useRef(false);
  const snapshot = useMemo(() => getOrderItemDraftData(state), [state]);
  const latestSnapshotRef = useRef<Data | null>(snapshot);
  const identityKey = useMemo(() => (identity ? getOrderItemDraftCacheKey(identity) : null), [identity]);
  const isDirty = hasDirtyFields(state.dirty);

  latestSnapshotRef.current = snapshot;

  useEffect(() => {
    loadedDraftKeyRef.current = null;
    setPendingDraft(null);
  }, [identityKey]);

  useEffect(() => {
    if (!identity || !identityKey || !state.lastPersistedSnapshot || loadedDraftKeyRef.current === identityKey) {
      return;
    }

    loadedDraftKeyRef.current = identityKey;
    const storedDraft = loadOrderItemDraft(identity);

    if (!storedDraft) {
      setPendingDraft(null);
      return;
    }

    if (storedDraft.baseETag === state.baseETag) {
      dispatch({ type: 'draftRestored', draft: storedDraft });
      setPendingDraft(null);
      return;
    }

    setPendingDraft(storedDraft);
  }, [identity, identityKey, state.baseETag, state.lastPersistedSnapshot]);

  useEffect(() => {
    if (!identity || !identityKey || !state.lastPersistedSnapshot || !snapshot) {
      return;
    }

    if (isDirty) {
      saveOrderItemDraft(identity, {
        baseETag: state.baseETag,
        dirtyFields: state.dirty,
        draftData: snapshot
      });
      return;
    }

    if (clearWhenCleanRef.current) {
      clearOrderItemDraft(identity);
      clearWhenCleanRef.current = false;
    }
  }, [identity, identityKey, isDirty, snapshot, state.baseETag, state.dirty, state.lastPersistedSnapshot]);

  const restorePendingDraft = useCallback(() => {
    if (!pendingDraft) {
      return;
    }

    dispatch({ type: 'draftRestored', draft: pendingDraft });
    setPendingDraft(null);
  }, [pendingDraft]);

  const discardPendingDraft = useCallback(() => {
    if (identity) {
      clearOrderItemDraft(identity);
    }

    setPendingDraft(null);
  }, [identity]);

  return {
    state,
    snapshot,
    pendingDraft,
    answers: state.answers,
    tokens: state.tokens,
    address: state.address,
    attachments: state.attachments,
    dirty: state.dirty,
    conflicts: state.conflicts,
    baseETag: state.baseETag,
    lastPersistedSnapshot: state.lastPersistedSnapshot,
    applyServerSnapshot: useCallback((snapshot: Data, etag: string | null) => {
      dispatch({ type: 'serverSnapshotReceived', snapshot, etag });
    }, []),
    markSnapshotPersisted: useCallback(
      (snapshot: Data, etag: string | null) => {
        const latestSnapshot = latestSnapshotRef.current;

        if (identity && latestSnapshot && areDataEqual(latestSnapshot, snapshot)) {
          clearOrderItemDraft(identity);
          clearWhenCleanRef.current = false;
        } else {
          clearWhenCleanRef.current = true;
        }

        dispatch({ type: 'snapshotPersisted', snapshot, etag });
      },
      [identity]
    ),
    setMergeConflicts: useCallback((conflicts: DataMergeConflict[]) => {
      dispatch({ type: 'mergeConflictsDetected', conflicts });
    }, []),
    clearMergeConflicts: useCallback(() => {
      dispatch({ type: 'mergeConflictsCleared' });
    }, []),
    restorePendingDraft,
    discardPendingDraft,
    setAnswerValue: useCallback((questionId: string, value: string) => {
      dispatch({ type: 'answerChanged', questionId, value });
    }, []),
    setTokenValue: useCallback((questionId: string, token: string, value: any) => {
      dispatch({ type: 'tokenChanged', questionId, token, value });
    }, []),
    setAddressValue: useCallback((value: string) => {
      dispatch({ type: 'addressChanged', value });
    }, []),
    setAttachments: useCallback((attachments: string[]) => {
      dispatch({ type: 'attachmentsChanged', attachments });
    }, [])
  };
};
